import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Header from '../components/Header';
import { signInAnonymously } from '../utils/authUtils';
import { createRoom, joinRoom } from '../utils/roomUtils';
import { CardDeckType } from '../utils/types';
import '../styles/main.css';

const HomePage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'join' | 'create'>('create');
    const [roomName, setRoomName] = useState('');
    const [userName, setUserName] = useState('');
    const [roomCode, setRoomCode] = useState('');
    const [joinUserName, setJoinUserName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [deckType, setDeckType] = useState<CardDeckType>('fibonacci');
    const [customValues, setCustomValues] = useState<string>('');
    const [showCustomDeck, setShowCustomDeck] = useState(false);

    const history = useHistory();

    // Initialize anonymous auth when the component loads
    useEffect(() => {
        const initAuth = async () => {
            try {
                await signInAnonymously();

                // Check for room code in URL params
                const urlParams = new URLSearchParams(window.location.search);
                const joinRoomCode = urlParams.get('join');
                if (joinRoomCode) {
                    setActiveTab('join');
                    setRoomCode(joinRoomCode);
                }

                // Check if there's a recent room in localStorage
                const lastRoom = localStorage.getItem('pp_lastRoom');
                const lastUserName = lastRoom
                    ? localStorage.getItem(`pp_${lastRoom}_userName`)
                    : null;

                if (lastUserName) {
                    setUserName(lastUserName);
                    setJoinUserName(lastUserName);
                }
            } catch (error) {
                console.error('Error initializing auth:', error);
            }
        };

        initAuth();
    }, []);

    const handleCreateRoom = async () => {
        setError('');

        if (!roomName.trim()) {
            setError('Please enter a room name');
            return;
        }

        if (!userName.trim()) {
            setError('Please enter your name');
            return;
        }

        try {
            setLoading(true);
            console.log(
                'Creating room with name:',
                roomName.trim(),
                'and username:',
                userName.trim(),
            );

            let customValueArray: string[] = [];
            if (deckType === 'custom' && customValues.trim()) {
                customValueArray = customValues
                    .split(',')
                    .map((v) => v.trim())
                    .filter((v) => v.length > 0);

                if (customValueArray.length === 0) {
                    setError(
                        'Please enter valid custom card values or select a different deck type',
                    );
                    setLoading(false);
                    return;
                }
            }

            const { roomCode: newRoomCode } = await createRoom(
                roomName.trim(),
                userName.trim(),
                deckType,
                deckType === 'custom' ? customValueArray : undefined,
            );

            history.push(`/room/${newRoomCode}`);
        } catch (err) {
            setError('Failed to create room. Please try again.');
            console.error('Room creation error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleJoinRoom = async () => {
        setError('');

        if (!roomCode.trim()) {
            setError('Please enter a room code');
            return;
        }

        if (!joinUserName.trim()) {
            setError('Please enter your name');
            return;
        }

        try {
            setLoading(true);
            const formattedRoomCode = roomCode.trim().toUpperCase();

            await joinRoom(formattedRoomCode, joinUserName.trim());
            history.push(`/room/${formattedRoomCode}`);
        } catch (err) {
            setError(
                'Failed to join room. Please check your room code and try again.',
            );
            console.error('Room join error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeckTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = e.target.value as CardDeckType;
        setDeckType(newType);
        setShowCustomDeck(newType === 'custom');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
            <Header />
            <div className="max-w-md mx-auto p-6">
                <div className="bg-white dark:bg-dark-card rounded-lg shadow-lg overflow-hidden">
                    <div className="flex border-b border-gray-200 dark:border-dark-border">
                        <button
                            onClick={() => setActiveTab('create')}
                            className={`flex-1 py-3 font-medium transition-colors duration-200 ${
                                activeTab === 'create'
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                        >
                            Create Room
                        </button>
                        <button
                            onClick={() => setActiveTab('join')}
                            className={`flex-1 py-3 font-medium transition-colors duration-200 ${
                                activeTab === 'join'
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                        >
                            Join Room
                        </button>
                    </div>

                    <div className="p-6">
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md border border-red-200 dark:border-red-800">
                                {error}
                            </div>
                        )}

                        {activeTab === 'create' ? (
                            <div className="space-y-4">
                                <h2 className="text-xl font-bold text-center mb-6 dark:text-dark-text">
                                    Create a New Room
                                </h2>

                                <div>
                                    <label
                                        htmlFor="roomName"
                                        className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        Room Name
                                    </label>
                                    <input
                                        id="roomName"
                                        type="text"
                                        value={roomName}
                                        onChange={(e) =>
                                            setRoomName(e.target.value)
                                        }
                                        placeholder="Planning Poker Room"
                                        className="input dark:bg-dark-card dark:border-dark-border dark:text-dark-text"
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="userName"
                                        className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        Your Name
                                    </label>
                                    <input
                                        id="userName"
                                        type="text"
                                        value={userName}
                                        onChange={(e) =>
                                            setUserName(e.target.value)
                                        }
                                        placeholder="John Doe"
                                        className="input dark:bg-dark-card dark:border-dark-border dark:text-dark-text"
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="deckType"
                                        className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        Card Deck Type
                                    </label>
                                    <select
                                        id="deckType"
                                        value={deckType}
                                        onChange={handleDeckTypeChange}
                                        className="select dark:bg-dark-card dark:border-dark-border dark:text-dark-text"
                                    >
                                        <option value="fibonacci">
                                            Fibonacci (0, 1, 2, 3, 5, 8, 13, 21,
                                            34, ?)
                                        </option>
                                        <option value="bau">
                                            BAU (1, 2, 3, 4, 5, 6, 7, 8, 9, 10)
                                        </option>
                                        <option value="tshirt">
                                            T-Shirt Sizes (XXS, XS, S, M, L, XL,
                                            ?)
                                        </option>
                                        <option value="powers">
                                            Powers of 2 (0, 1, 2, 4, 8, 16, 32,
                                            64, ?)
                                        </option>
                                        <option value="custom">
                                            Custom Values
                                        </option>
                                    </select>
                                </div>

                                {showCustomDeck && (
                                    <div>
                                        <label
                                            htmlFor="customValues"
                                            className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Custom Card Values (comma-separated)
                                        </label>
                                        <input
                                            id="customValues"
                                            type="text"
                                            value={customValues}
                                            onChange={(e) =>
                                                setCustomValues(e.target.value)
                                            }
                                            placeholder="1, 2, 3, 5, 8, 13, ?"
                                            className="input dark:bg-dark-card dark:border-dark-border dark:text-dark-text"
                                        />
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            Enter values separated by commas.
                                            Include "?" for unknown estimates.
                                        </p>
                                    </div>
                                )}

                                <button
                                    onClick={handleCreateRoom}
                                    disabled={loading}
                                    className={`w-full btn ${
                                        loading
                                            ? 'bg-opacity-70 cursor-not-allowed'
                                            : ''
                                    } bg-secondary text-white py-3 rounded-md font-medium transition-all duration-200 hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-opacity-50`}
                                >
                                    {loading ? (
                                        <div className="flex items-center justify-center">
                                            <svg
                                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                            Creating...
                                        </div>
                                    ) : (
                                        'Create Room'
                                    )}
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <h2 className="text-xl font-bold text-center mb-6 dark:text-dark-text">
                                    Join an Existing Room
                                </h2>

                                <div>
                                    <label
                                        htmlFor="roomCode"
                                        className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        Room Code
                                    </label>
                                    <input
                                        id="roomCode"
                                        type="text"
                                        value={roomCode}
                                        onChange={(e) =>
                                            setRoomCode(e.target.value)
                                        }
                                        placeholder="ABCDEF"
                                        className="input uppercase dark:bg-dark-card dark:border-dark-border dark:text-dark-text"
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="joinUserName"
                                        className="block mb-2 font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        Your Name
                                    </label>
                                    <input
                                        id="joinUserName"
                                        type="text"
                                        value={joinUserName}
                                        onChange={(e) =>
                                            setJoinUserName(e.target.value)
                                        }
                                        placeholder="John Doe"
                                        className="input dark:bg-dark-card dark:border-dark-border dark:text-dark-text"
                                    />
                                </div>

                                <button
                                    onClick={handleJoinRoom}
                                    disabled={loading}
                                    className={`w-full btn ${
                                        loading
                                            ? 'bg-opacity-70 cursor-not-allowed'
                                            : ''
                                    } bg-primary text-white py-3 rounded-md font-medium transition-all duration-200 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50`}
                                >
                                    {loading ? (
                                        <div className="flex items-center justify-center">
                                            <svg
                                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                            Joining...
                                        </div>
                                    ) : (
                                        'Join Room'
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;
